import React from "react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import {
    STOP_POPUP_ARRIVAL_CHIP_CLASS_NAME,
    STOP_POPUP_ARRIVAL_HEADSIGN_CLASS_NAME,
    STOP_POPUP_ARRIVAL_MAIN_ROW_CLASS_NAME,
    STOP_POPUP_ARRIVAL_PER_ARRIVAL_QUAI_BADGE_CLASS_NAME,
    STOP_POPUP_ARRIVAL_ROUTE_BADGE_CLASS_NAME,
    STOP_POPUP_ARRIVAL_ROUTE_BADGE_MIN_WIDTH_PX,
    STOP_POPUP_ARRIVAL_ROW_SPACING_CLASS_NAME,
    STOP_POPUP_ARRIVAL_SUPPLIER_ROW_CLASS_NAME,
    STOP_POPUP_ARRIVAL_TIME_PILL_CLASS_NAME,
    STOP_POPUP_ARRIVAL_TIME_ROW_CLASS_NAME,
    STOP_POPUP_ARRIVALS_LIST_MAX_HEIGHT_PX,
    STOP_POPUP_COUNTDOWN_PILL_VALUE_CLASS_NAME,
    STOP_POPUP_LOADING_SKELETON_ROW_COUNT,
} from "./stopPopup.layout.constants";

interface StopPopupLoadingSkeletonProps {

    /** Number of placeholder rows shown while arrivals load. */
    "rowCount"?: number;

    /** Whether dark overlay palette is active. */
    "isDarkUi": boolean;

    /** Parent station flag — skeleton shows a quai badge placeholder when true. */
    "isParentStop"?: boolean;
}

interface StopPopupLoadingSkeletonTheme {
    "chipBackground": string;
    "chipBorder":     string;
    "pillBackground": string;
    "pillBorder":     string;
    "shimmer":        string;
    "dividerColor":   string;
}

/**
 * Resolves skeleton shimmer colors aligned with stop popup chips.
 *
 * @param isDarkUi Whether dark overlay palette is active.
 * @returns Skeleton theme tokens.
 */
const getStopPopupLoadingSkeletonTheme = ( isDarkUi: boolean ): StopPopupLoadingSkeletonTheme => {
    if ( isDarkUi ) {
        const palette = getUIColorPalette( true );

        return {
            "chipBackground": palette.overlayCardBackgroundSoft,
            "chipBorder":     palette.overlayBorder,
            "dividerColor":   palette.overlayBorder,
            "pillBackground": palette.overlayCardBackgroundStrong,
            "pillBorder":     palette.overlayBorder,
            "shimmer":        palette.overlayTextSecondary,
        };
    }

    return {
        "chipBackground": "rgba(255, 255, 255, 0.7)",
        "chipBorder":     "rgba(0, 0, 0, 0.05)",
        "dividerColor":   "rgba(0, 0, 0, 0.05)",
        "pillBackground": "rgba(255, 255, 255, 0.7)",
        "pillBorder":     "rgba(0, 0, 0, 0.1)",
        "shimmer":        "#9CA3AF",
    };
};

interface StopPopupArrivalRowSkeletonProps {

    /** Row index for width variation. */
    "rowIndex": number;

    /** Parent station flag — mirrors per-arrival quai badge slot. */
    "isParentStop": boolean;

    /** Skeleton theme tokens. */
    "theme": StopPopupLoadingSkeletonTheme;
}

/**
 * Single skeleton row mirroring the real arrival chip layout:
 * icon + line badge + headsign/operator block, divider, time pill.
 *
 * @param props Row skeleton props.
 * @returns One skeleton arrival row.
 */
const StopPopupArrivalRowSkeleton: React.FC<StopPopupArrivalRowSkeletonProps> = ( {
    isParentStop,
    rowIndex,
    theme,
} ) => (
    <div
        aria-hidden
        className={`${ STOP_POPUP_ARRIVAL_CHIP_CLASS_NAME } ${ STOP_POPUP_ARRIVAL_ROW_SPACING_CLASS_NAME }`}
        style={{
            "backgroundColor": theme.chipBackground,
            "borderColor":     theme.chipBorder,
        }}
    >
        <div className={STOP_POPUP_ARRIVAL_MAIN_ROW_CLASS_NAME}>
            <div
                className="inline-block h-4 w-4 flex-shrink-0 rounded-full"
                style={{
                    "backgroundColor": theme.shimmer,
                    "opacity":         0.35,
                }}
            />
            <div
                className={STOP_POPUP_ARRIVAL_ROUTE_BADGE_CLASS_NAME}
                style={{
                    "backgroundColor": theme.shimmer,
                    "minWidth":        `${ STOP_POPUP_ARRIVAL_ROUTE_BADGE_MIN_WIDTH_PX }px`,
                    "opacity":         0.45,
                }}
            >
                <span
                    aria-hidden
                    className="invisible select-none"
                >
                    00
                </span>
            </div>
            <div className="min-w-0 flex-1">
                <div
                    className={STOP_POPUP_ARRIVAL_HEADSIGN_CLASS_NAME}
                    style={{
                        "backgroundColor": theme.shimmer,
                        "opacity":         0.4,
                        "width":           rowIndex % 2 === 0 ? "82%" : "68%",
                    }}
                >
                    <span
                        aria-hidden
                        className="invisible select-none"
                    >
                        .
                    </span>
                </div>
                <div className={STOP_POPUP_ARRIVAL_SUPPLIER_ROW_CLASS_NAME}>
                    <div
                        className="min-w-0 flex-1 truncate text-xs"
                        style={{
                            "backgroundColor": theme.shimmer,
                            "opacity":         0.28,
                            "width":           "46%",
                        }}
                    >
                        <span
                            aria-hidden
                            className="invisible select-none"
                        >
                            {"\u00a0"}
                        </span>
                    </div>
                    {isParentStop
                        ? (
                            <span
                                className={`${ STOP_POPUP_ARRIVAL_PER_ARRIVAL_QUAI_BADGE_CLASS_NAME } flex-shrink-0 border px-2 py-0.5`}
                                style={{
                                    "backgroundColor": theme.shimmer,
                                    "borderColor":     theme.pillBorder,
                                    "opacity":         0.32,
                                }}
                            >
                                <span
                                    aria-hidden
                                    className="invisible select-none"
                                >
                                    Quai 0
                                </span>
                            </span>
                        )
                        : null}
                </div>
            </div>
        </div>
        <div
            className="border-t"
            style={{ "borderColor": theme.dividerColor }}
        />
        <div className={STOP_POPUP_ARRIVAL_TIME_ROW_CLASS_NAME}>
            <div
                className={STOP_POPUP_ARRIVAL_TIME_PILL_CLASS_NAME}
                style={{
                    "backgroundColor": theme.pillBackground,
                    "borderColor":     theme.pillBorder,
                }}
            >
                <div
                    className="h-4 w-4 flex-shrink-0 rounded-full"
                    style={{
                        "backgroundColor": theme.shimmer,
                        "opacity":         0.35,
                    }}
                />
                <span className={STOP_POPUP_COUNTDOWN_PILL_VALUE_CLASS_NAME}>
                    <span
                        aria-hidden
                        className="invisible select-none"
                    >
                        00&apos;
                    </span>
                </span>
            </div>
            <span
                className="font-bold"
                style={{
                    "backgroundColor": theme.shimmer,
                    "opacity":         0.4,
                }}
            >
                <span
                    aria-hidden
                    className="invisible select-none"
                >
                    00:00
                </span>
            </span>
        </div>
    </div>
);

/**
 * Placeholder rows for the shared arrivals list while routes load.
 *
 * @param props Skeleton row props.
 * @returns Skeleton rows without an outer list shell.
 */
export const StopPopupLoadingSkeletonRows: React.FC<StopPopupLoadingSkeletonProps> = ( {
    isDarkUi,
    isParentStop = false,
    rowCount = STOP_POPUP_LOADING_SKELETON_ROW_COUNT,
} ) => {
    const theme = getStopPopupLoadingSkeletonTheme( isDarkUi );

    return (
        <>
            {Array.from( { "length": rowCount }, ( _, rowIndex ) => (
                <StopPopupArrivalRowSkeleton
                    isParentStop={isParentStop}
                    key={`stop-popup-skeleton-${ rowIndex }`}
                    rowIndex={rowIndex}
                    theme={theme}
                />
            ) )}
        </>
    );
};

/**
 * Standalone skeleton list clipped to arrivals max height (legacy shell).
 *
 * @param props Skeleton props.
 * @returns Loading skeleton list.
 */
const StopPopupLoadingSkeleton: React.FC<StopPopupLoadingSkeletonProps> = ( props ) => (
    <div
        aria-hidden
        className="flex w-full flex-col overflow-hidden"
        style={{ "maxHeight": `${ STOP_POPUP_ARRIVALS_LIST_MAX_HEIGHT_PX }px` }}
    >
        <StopPopupLoadingSkeletonRows {...props} />
    </div>
);

export default StopPopupLoadingSkeleton;
